/**
 * Pure helpers for the negotiation (Átrio) page.
 *
 * Extracted out of the large `negotiate.tsx` component so the bits with real
 * branching logic — highlight class resolution and the hall-of-cards cache
 * contract — can be unit-tested in isolation and kept free of shared-state
 * mutation bugs.
 */
import type { ConfirmedBid, HallCardWire } from "./negotiate.bidState";

/**
 * Flat record of the CSS class strings that decorate a highlighted card.
 *
 * `type` is intentionally widened to `string` here so this module stays
 * decoupled from `negotiate.tsx`'s narrower literal union; the generic on
 * {@link resolveHighlightClassName} preserves the caller's exact type.
 */
export interface HighlightClassEntry {
  type: string;
  container: string;
  post: string;
  userInfo: string;
  postPosition: string;
  reasonPosition: string;
  chipContainer: string;
  chipContent: string;
  chipPosition: string;
  chipValue: string;
}

/**
 * Resolve the highlight class entry for a card.
 *
 * - Returns `null` when the card is neither a top/flop highlight nor a
 *   power-offensive card.
 * - Returns the power-offensive template (unmodified) when the card is ONLY
 *   power-offensive.
 * - Returns a FRESH, merged entry (top/flop classes + power-offensive classes,
 *   `type` bumped to `"2+"`) when the card is both.
 *
 * Crucially it never mutates `positionTable`'s entries or `powerOffensiveTemplate`
 * — the previous in-place `+=` mutation corrupted the shared template objects,
 * unboundedly growing their class strings and bleeding the `"2+"` type onto
 * every other card sharing that highlight slot.
 */
export function resolveHighlightClassName<T extends HighlightClassEntry>(
  cardId: string,
  positionTable: Record<string, T>,
  powerOffensiveIds: string[],
  powerOffensiveTemplate: T,
): T | null {
  const positionEntry = positionTable[cardId];
  const isPowerOffensive = powerOffensiveIds.includes(cardId);

  if (!positionEntry) {
    return isPowerOffensive ? powerOffensiveTemplate : null;
  }

  if (!isPowerOffensive) {
    return positionEntry;
  }

  // Both a position highlight AND power-offensive: clone the position entry and
  // append the power-offensive classes onto the COPY.
  const merged = { ...positionEntry };
  (merged as HighlightClassEntry).type = "2+";
  merged.container += " " + powerOffensiveTemplate.container;
  merged.post += " " + powerOffensiveTemplate.post;
  merged.userInfo += " " + powerOffensiveTemplate.userInfo;
  merged.postPosition += " " + powerOffensiveTemplate.postPosition;
  merged.reasonPosition += " " + powerOffensiveTemplate.reasonPosition;
  merged.chipContainer += " " + powerOffensiveTemplate.chipContainer;
  merged.chipContent += " " + powerOffensiveTemplate.chipContent;
  merged.chipPosition += " " + powerOffensiveTemplate.chipPosition;
  merged.chipValue += " " + powerOffensiveTemplate.chipValue;
  return merged;
}

/**
 * Card ids for the six highlight slots, as delivered by `/hall-posts`.
 *
 * Any slot may be empty ("" or undefined) when the board is too small to fill
 * it without overlapping a top: see enterprise-ads-api `computeTopFlopIndices`,
 * which only emits a flop id once it is disjoint from the tops.
 */
export interface HighlightSlotIds {
  top1Id?: string;
  top2Id?: string;
  top3Id?: string;
  flop1Id?: string;
  flop2Id?: string;
  flop3Id?: string;
}

/**
 * The highlight template for each of the six slots, keyed the same way as the
 * ids in {@link HighlightSlotIds}.
 */
export interface HighlightSlotTemplates<T> {
  top1: T;
  top2: T;
  top3: T;
  flop1: T;
  flop2: T;
  flop3: T;
}

/**
 * Build the `cardId -> highlight template` lookup with TOP precedence over flop.
 *
 * The backend must never emit the same id as both a top and a flop (that is what
 * `computeTopFlopIndices` guards). This map is the second line of defense: if an
 * id ever lands in both slots, the TOP must win, so the destaque card keeps its
 * `type: "hits"` and stays visible under the default HITS filter, instead of
 * being rewritten to `type: "flops"` and vanishing from it.
 *
 * Precedence comes from write order (last write wins), walking the slots from
 * least to most important: flop3, flop2, flop1, top3, top2, top1. Empty or
 * omitted ids are skipped so an absent slot never claims the "" key and never
 * shadows a real card.
 */
export function buildHighlightPositionTable<T>(
  ids: HighlightSlotIds,
  templates: HighlightSlotTemplates<T>,
): Record<string, T> {
  const table: Record<string, T> = {};
  const orderedLowToHigh: Array<[string | undefined, T]> = [
    [ids.flop3Id, templates.flop3],
    [ids.flop2Id, templates.flop2],
    [ids.flop1Id, templates.flop1],
    [ids.top3Id, templates.top3],
    [ids.top2Id, templates.top2],
    [ids.top1Id, templates.top1],
  ];
  for (const [id, template] of orderedLowToHigh) {
    if (!id) continue;
    table[id] = template;
  }
  return table;
}

/**
 * Shape of the `/hall-posts` response we care about when (re)loading the board.
 *
 * enterprise-ads-api (ListHallPostsResp) omits every empty highlight id, and an
 * empty board arrives as `{"cards": null}`. `C` is the card type.
 */
export interface HallOfCardsPayload<C> extends HighlightSlotIds {
  cycleId?: string;
  sealed?: boolean;
  // null on the wire for an empty board, undefined once `cards?.map` read it.
  cards?: C[] | null;
  // enterprise-ads-api omits the key when the org has no power-offensive post.
  powerOffensiveIds?: string[];
}

/**
 * The `/hall-posts` response as it arrives. A post nobody has bid on still
 * carries null bid fields here, so these cards are not accepted by
 * {@link mapHallOfCards} until withUnbidDefaults has filled them.
 */
export type HallPostsWire<T extends ConfirmedBid> = HallOfCardsPayload<HallCardWire<T>>;

export interface HallOfCardsSnapshot<C extends ConfirmedBid> extends HighlightSlotIds {
  cycleId?: string;
  sealed?: boolean;
  // Absent or null for an empty board: readers check it before indexing.
  cards?: C[] | null;
  hallFilter: "hits";
  powerOffensiveIds: string[];
  selectedBid: number;
}

/**
 * Whether `getHallOfCards` should perform a fetch.
 *
 * The guard MUST read the synchronously-updated cache (a ref), not React state:
 * a cycle restart clears the cache and immediately re-invokes the fetch in the
 * same tick, where the state closure would still hold the previous cycle's
 * value and wrongly short-circuit — leaving stale cards on screen until a
 * remount. Passing `undefined`/`null` (cleared cache) returns `true`.
 */
export function shouldFetchHallCards(cached: unknown): boolean {
  return !cached;
}

/**
 * How often the Átrio re-reads `/negotiate-time`.
 *
 * HTTP is the SOURCE OF TRUTH for the market window; the RTA `negotiateMarket`
 * event is only an accelerator that makes a transition visible sooner. The page
 * used to read the window exactly once per mount and then depend entirely on
 * that push, so an open tab that missed one event kept the previous window's
 * `endAt` forever and painted "Fechando o mercado..." indefinitely.
 *
 * 30 s is the slowest poll that still bounds the staleness of a window
 * transition to well under a cycle, at 2 requests/min/tab against a route that
 * only reads a Redis hash.
 */
export const MARKET_PERIOD_POLL_MS = 30_000;

/**
 * Earliest year a market deadline may plausibly carry.
 *
 * `GetNegotiatePeriod` formats a MISSING `end_at` as `unixToRFC3339(0)`, i.e.
 * `"0001-01-01T00:00:00Z"`. That string parses to a perfectly VALID `Date`, so
 * a `Number.isNaN` check alone does not catch it — it would silently be adopted
 * as an already-elapsed deadline and paint "Fechando o mercado..." on a fresh
 * load. Reject the absurd year instead of inheriting it.
 */
export const MIN_PLAUSIBLE_MARKET_YEAR = 2000;

/**
 * Parse the `endAt` field of `/negotiate-time` into a usable deadline.
 *
 * Returns `undefined` — meaning "the window is UNKNOWN" — for anything that is
 * not a plausible deadline: a missing/empty value, an unparseable string, or
 * the year-1 sentinel the backend emits when the Redis period hash has no
 * `end_at`. "Unknown" is deliberately a distinct outcome from "elapsed": the
 * caller must not collapse the two (see {@link marketCountdown}).
 */
export function parseMarketEndAt(raw: unknown): Date | undefined {
  if (typeof raw !== "string" && !(raw instanceof Date)) return undefined;
  const parsed = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(parsed.getTime())) return undefined;
  if (parsed.getUTCFullYear() < MIN_PLAUSIBLE_MARKET_YEAR) return undefined;
  return parsed;
}

/**
 * How long a deadline may sit in the past and still honestly be called
 * "closing".
 *
 * "Closing" is a claim about the cycle: the window ran out and the backend is
 * about to hand over the next one. That claim only holds while the next
 * authoritative read is still pending. Once a full poll cycle (plus one retry
 * of slack) has come and gone without a fresh window, what the page is holding
 * is not a closing market, it is a stale deadline, and saying "closing" is a
 * lie that never ends on its own.
 *
 * Derived from {@link MARKET_PERIOD_POLL_MS} on purpose, NOT from the length of
 * the market window. A fraction of the window would be a different number in
 * production (6 h) than in demo_mode (about 5 min), and the whole point is that
 * the grace must mean the same thing in both: "one refresh has had its chance".
 */
export const MARKET_CLOSING_GRACE_MS = 2 * MARKET_PERIOD_POLL_MS;

/**
 * The market countdown has four states, not two.
 *
 * - `counting`: a known deadline that is still in the future.
 * - `elapsed`: a known deadline that passed within
 *   {@link MARKET_CLOSING_GRACE_MS}. ONLY this state may be rendered as
 *   "Fechando o mercado...".
 * - `stale`: a known deadline that passed longer ago than the grace. The
 *   handover the page promised never arrived, so the deadline is evidence of
 *   nothing. Measured on the dev eco 2026-08-05: the cycle machinery went
 *   5 min 07 s (05:02:38Z to 05:07:45Z) without publishing any period, while
 *   `/negotiate-time` kept answering `period=negotiate`. Every second of that
 *   was painted as a closing market.
 * - `unknown`: no usable deadline at all. The original code computed `diff = 0`
 *   here and fell into the same `else` branch as `elapsed`, which is how an
 *   absent window was reported to the user as a closing market.
 *
 * `stale` and `unknown` stay distinct because they are distinct facts: one had
 * a deadline and it expired, the other never had one. The Átrio happens to
 * render both as "the window is being resynchronised", which is exactly what is
 * true of both, but that is a rendering choice and not a reason to merge them.
 */
export type MarketCountdownStatus = "counting" | "elapsed" | "stale" | "unknown";

export interface MarketCountdown {
  status: MarketCountdownStatus;
  /** Milliseconds left, or `undefined` when the deadline is unknown. */
  remainingMs: number | undefined;
}

/**
 * Classify the market countdown from a (possibly absent) deadline and `now`.
 */
export function marketCountdown(endAt: Date | undefined, now: Date): MarketCountdown {
  if (!endAt) return { status: "unknown", remainingMs: undefined };
  const remainingMs = endAt.getTime() - now.getTime();
  if (remainingMs > 0) return { status: "counting", remainingMs };
  if (-remainingMs > MARKET_CLOSING_GRACE_MS) return { status: "stale", remainingMs };
  return { status: "elapsed", remainingMs };
}

/**
 * Build the in-memory hall-of-cards snapshot from the API payload.
 */
export function mapHallOfCards<C extends ConfirmedBid>(
  payload: HallOfCardsPayload<C>,
): HallOfCardsSnapshot<C> {
  return {
    ...(typeof payload.cycleId === "string" && payload.cycleId ? { cycleId: payload.cycleId } : {}),
    ...(typeof payload.sealed === "boolean" ? { sealed: payload.sealed } : {}),
    cards: payload.cards,
    hallFilter: "hits",
    top1Id: payload.top1Id,
    top2Id: payload.top2Id,
    top3Id: payload.top3Id,
    flop1Id: payload.flop1Id,
    flop2Id: payload.flop2Id,
    flop3Id: payload.flop3Id,
    powerOffensiveIds: payload.powerOffensiveIds ?? [],
    selectedBid: 0,
  };
}

/**
 * Absolute, idempotent record of the posts an org is currently winning:
 * `postId -> the org's winning bid amount on that post`.
 *
 * It is the source of truth the negotiation summary is derived from, kept as a
 * per-post map (like the outer `totalBidsAmount` state) precisely so it can be
 * patched absolutely and never drifts.
 */
export type OrgWinningBids = Record<string, number>;

export interface OrgTaxAggregate {
  totalBidsAmount: number;
  winningBidsCount: number;
}

/**
 * Apply one incoming bid to the org's winning-bids record ABSOLUTELY.
 *
 * If the org now holds the post, its amount is written (overwriting whatever was
 * there); otherwise the post is dropped. This upsert/delete is idempotent:
 * replaying the same event is a no-op, and a single dropped event only loses
 * that one post until the next event or a board rebuild. The old handler
 * accumulated the aggregate with `+=` / `-=`, where one duplicated or missed
 * event corrupted the running total for the rest of the cycle.
 */
export function applyOrgWinningBid(
  record: OrgWinningBids,
  postId: string,
  lastBidOrg: string | undefined,
  lastBidAmount: number,
  orgId: string | undefined,
): OrgWinningBids {
  const next = { ...record };
  if (orgId !== undefined && lastBidOrg === orgId) {
    next[postId] = lastBidAmount;
  } else {
    delete next[postId];
  }
  return next;
}

/**
 * Derive the negotiation summary from the absolute winning-bids record.
 *
 * Always recomputed from scratch over the record, never accumulated, so the
 * numbers are a pure function of the current record and cannot drift.
 */
export function summarizeOrgWinningBids(record: OrgWinningBids): OrgTaxAggregate {
  const amounts = Object.values(record);
  return {
    totalBidsAmount: amounts.reduce((acc, amount) => acc + amount, 0),
    winningBidsCount: amounts.length,
  };
}

/**
 * Build the absolute winning-bids record from a board of cards.
 *
 * Mirrors the initial reduce the component ran in `initialize()` and the rebuild
 * in `getHallOfCards`, so the running record starts from the same absolute truth
 * that the per-event patches then keep idempotent.
 */
export function buildOrgWinningBids(
  cards: Array<{ id: string; lastBidOrg?: string; lastBidAmount: number }>,
  orgId: string | undefined,
): OrgWinningBids {
  const record: OrgWinningBids = {};
  if (orgId === undefined) return record;
  for (const card of cards) {
    if (card.lastBidOrg === orgId) {
      record[card.id] = card.lastBidAmount;
    }
  }
  return record;
}
